function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('.menu-drawer-container');
    this.mainToggleButton = this.querySelector('.menu-drawer__trigger');
    this.menuDrawer = this.querySelector('#menu-drawer');
    if (!this.mainDetailsToggle || !this.mainToggleButton) return;

    this.enforceFocus = this.enforceFocus.bind(this);
    this.onOutsideClick = this.onOutsideClick.bind(this);
    this.onMainToggleClick = this.onMainToggleClick.bind(this);
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.handleSummaryKeydown = this.handleSummaryKeydown.bind(this);
    this.handleSummaryKeyup = this.handleSummaryKeyup.bind(this);
    this.onNestedSummaryPointerDown = this.onNestedSummaryPointerDown.bind(this);
    this.onSummaryKeyboardClickCapture = this.onSummaryKeyboardClickCapture.bind(this);
    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.mainToggleButton.addEventListener('click', this.onMainToggleClick);

    this.querySelectorAll('details > summary').forEach((summary) => {
      summary.addEventListener('click', this.onSummaryClick);

      const isSubmenuToggle = summary.classList.contains('menu-drawer__menu-item');

      if (isSubmenuToggle) {
        summary.addEventListener('keydown', this.handleSummaryKeydown);
        summary.addEventListener('keyup', this.handleSummaryKeyup);
        summary.addEventListener('click', this.onSummaryKeyboardClickCapture, { capture: true });
        summary.addEventListener('pointerdown', this.onNestedSummaryPointerDown);
      }
    });
    this.querySelectorAll(
      'button:not(.menu-drawer__trigger):not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onMainToggleClick(event) {
    const isOpen = this.mainDetailsToggle.hasAttribute('open');

    if (isOpen) {
      this.closeMenuDrawer(event, this.mainToggleButton);
      return;
    }

    this.menuDrawer?.removeAttribute('hidden');
    this.mainDetailsToggle.setAttribute('open', '');
    this.openMenuDrawer(this.mainToggleButton);

    if (window.matchMedia('(max-width: 990px)').matches) {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (openDetailsElement) {
      this.closeSubmenu(openDetailsElement);
      return;
    }

    if (this.mainDetailsToggle.hasAttribute('open') && this.mainDetailsToggle.contains(event.target)) {
      this.closeMenuDrawer(event, this.mainToggleButton);
    }
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const triggeredByKeyboard = summaryElement.dataset.keyboardActivated === 'true' || event.detail === 0;
    const triggeredByPointer = summaryElement.dataset.pointerActivated === 'true' || event.detail > 0;
    delete summaryElement.dataset.keyboardActivated;
    delete summaryElement.dataset.pointerActivated;

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    event.preventDefault();
    event.stopPropagation();

    if (triggeredByPointer) {
      summaryElement.focus({ preventScroll: true });
    }

    if (!triggeredByKeyboard && !triggeredByPointer) {
      return;
    }

    if (isOpen) {
      this.closeSubmenu(detailsElement);
      return;
    }

    detailsElement.setAttribute('open', '');

    setTimeout(() => {
      detailsElement.classList.add('menu-opening');
      summaryElement.setAttribute('aria-expanded', true);
      parentMenuElement && parentMenuElement.classList.add('submenu-open');
      !reducedMotion || reducedMotion.matches
        ? addTrapFocus()
        : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
    }, 100);
  }

  openMenuDrawer(triggerElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    trapFocus(this.mainDetailsToggle, triggerElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    document.addEventListener('focusin', this.enforceFocus);
    document.addEventListener('click', this.onOutsideClick);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (!this.mainDetailsToggle || event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    document.removeEventListener('focusin', this.enforceFocus);
    document.removeEventListener('click', this.onOutsideClick);
    this.closeAnimation(this.mainDetailsToggle, () => {
      this.menuDrawer?.setAttribute('hidden', '');
    });
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.mainDetailsToggle.hasAttribute('open')) return;

      const activeElement = document.activeElement;
      if (!activeElement || activeElement === document.body) return;

      if (!this.mainDetailsToggle.contains(activeElement)) {
        this.closeMenuDrawer(new Event('focusout'), this.mainToggleButton);
      }
    });
  }

  enforceFocus(event) {
    if (!this.mainDetailsToggle.hasAttribute('open')) return;
    if (this.mainDetailsToggle.contains(event.target)) return;

    const focusableElements = getFocusableElements(this.mainDetailsToggle);
    if (focusableElements.length === 0) return;

    event.preventDefault();
    focusableElements[0].focus();
  }

  onOutsideClick(event) {
    if (!this.mainDetailsToggle.hasAttribute('open')) return;
    if (this.mainDetailsToggle.contains(event.target)) return;
    this.closeMenuDrawer(event, this.mainToggleButton);
  }

  handleSummaryKeydown(event) {
    const summary = event.currentTarget;
    if (summary.parentNode === this.mainDetailsToggle) return;
    const key = event.key?.toUpperCase();
    const code = event.code?.toUpperCase();
    const isEnter = key === 'ENTER' || code === 'ENTER';
    const isSpace = key === ' ' || key === 'SPACE' || key === 'SPACEBAR' || code === 'SPACE';

    if (!isEnter && !isSpace) return;

    event.preventDefault();
    event.stopPropagation();

    if (isEnter) {
      summary.dataset.enterPressed = 'true';
      this.activateSummaryFromKeyboard(summary);
      return;
    }

    summary.dataset.spacePressed = 'true';
  }

  handleSummaryKeyup(event) {
    const summary = event.currentTarget;
    if (summary.parentNode === this.mainDetailsToggle) return;
    const key = event.key?.toUpperCase();
    const code = event.code?.toUpperCase();
    const isEnter = key === 'ENTER' || code === 'ENTER';
    const isSpace = key === ' ' || key === 'SPACE' || key === 'SPACEBAR' || code === 'SPACE';

    if (isEnter && summary.dataset.enterPressed === 'true') {
      event.preventDefault();
      event.stopPropagation();
      delete summary.dataset.enterPressed;
      return;
    }

    if (!isSpace || summary.dataset.spacePressed !== 'true') return;

    event.preventDefault();
    event.stopPropagation();
    delete summary.dataset.spacePressed;
    this.activateSummaryFromKeyboard(summary);
  }

  activateSummaryFromKeyboard(summary) {
    summary.dataset.keyboardActivated = 'true';
    const syntheticEvent = {
      currentTarget: summary,
      preventDefault: () => {},
      stopPropagation: () => {},
      detail: 0,
    };
    this.onSummaryClick(syntheticEvent);
  }

  onNestedSummaryPointerDown(event) {
    if (event.currentTarget.parentNode === this.mainDetailsToggle) return;
    event.preventDefault();
    event.currentTarget.dataset.pointerActivated = 'true';
  }

  onSummaryKeyboardClickCapture(event) {
    if (event.currentTarget.parentNode === this.mainDetailsToggle) return;
    if (event.detail !== 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    if (!detailsElement) return;
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    if (!detailsElement) return;

    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(element, onComplete = null) {
    if (!element) return;

    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        element.removeAttribute('open');
        const parentOpenDetails = element.closest('details[open]');
        if (parentOpenDetails) {
          trapFocus(parentOpenDetails, element.querySelector('summary'));
        }
        if (typeof onComplete === 'function') onComplete();
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}


// ADA #61 - Hide decorative SVG icons in quantity +/- buttons from screen readers
function hideQuantitySVGs(root = document) {
  root.querySelectorAll('.quantity__button .svg-wrapper svg:not([aria-hidden])').forEach(svg => {
    svg.setAttribute('aria-hidden', 'true');
  });
}

hideQuantitySVGs();

const observer = new MutationObserver(() => hideQuantitySVGs());
observer.observe(document.body, { childList: true, subtree: true });


// =============================================================================
// ADA COMPLIANCE FIX: Add screen reader instructions to product carousels
// Issue #50: Carousels need screen-reader-only instructions
// Issue #165: Only add carousel instructions when actually functioning as carousel
// =============================================================================
(function() {
  'use strict';
  
  function addCarouselInstructions() {
    // ADA #165: Only add instructions to elements that are ACTUALLY functioning as carousels
    // Target product media galleries (carousels) - Dawn 15.4 specific
    const carousels = document.querySelectorAll(
      'media-gallery, [id*="GalleryViewer"], .product__media-wrapper, slider-component'
    );
    
    carousels.forEach(function(carousel) {
      // Check if instructions already added to avoid duplicates
      if (carousel.querySelector('.carousel-instructions')) return;
      
      // Check if this is actually a carousel (has navigation or multiple items)
      const slides = carousel.querySelectorAll('.product__media-item, [data-media-id], li[id*="Slide-"]');
      const hasNavButtons = carousel.querySelector('button[name="previous"], button[name="next"], .slider-button');
      
      // ADA #165: Additional check - skip if buttons are hidden or disabled
      // This prevents adding carousel markup to static grids (like "YOU MAY ALSO LIKE" when not sliding)
      let buttonsAreVisible = false;
      if (hasNavButtons) {
        const prevBtn = carousel.querySelector('button[name="previous"]');
        const nextBtn = carousel.querySelector('button[name="next"]');
        
        // Check if buttons are visible (not display:none, not hidden, not in a hidden parent)
        if (prevBtn || nextBtn) {
          const btnToCheck = prevBtn || nextBtn;
          const btnStyles = window.getComputedStyle(btnToCheck);
          const parentStyles = btnToCheck.parentElement ? window.getComputedStyle(btnToCheck.parentElement) : null;
          
          // Button is visible only if it AND its parent are not hidden
          buttonsAreVisible = btnStyles.display !== 'none' && 
                             btnStyles.visibility !== 'hidden' &&
                             (!parentStyles || (parentStyles.display !== 'none' && parentStyles.visibility !== 'hidden'));
        }
      }
      
      // Skip if not enough slides OR no visible nav buttons (not a functional carousel)
      if (slides.length <= 1 || !buttonsAreVisible) return;
      
      // Create screen-reader-only instructions
      const instructions = document.createElement('div');
      instructions.className = 'carousel-instructions visually-hidden';
      instructions.setAttribute('aria-live', 'polite');
      instructions.setAttribute('aria-atomic', 'true');
      
      const totalSlides = slides.length;
      instructions.textContent = 
        'Image gallery with ' + totalSlides + ' images. ' +
        'Use the Next and Previous buttons to navigate through the product images. ' +
        'Click on an image to view it in a larger lightbox.';
      
      // Insert as first child of carousel element
      carousel.insertBefore(instructions, carousel.firstChild);
      
      // ADA #163: Commented out - this was adding duplicate role="region" to slider-component elements
      // The role="region" is already on the <media-gallery> element in product-media-gallery.liquid (line 48)
      // Keeping this code was causing THREE instances of role="region" instead of one
      /*
      // Also update the carousel region label if missing
      if (!carousel.hasAttribute('aria-label') && !carousel.hasAttribute('aria-labelledby')) {
        carousel.setAttribute('aria-label', 'Product image gallery');
        carousel.setAttribute('role', 'region');
      }
      */
    });
  }
  
  function labelCarouselButtons() {
    // Ensure prev/next buttons have proper aria-labels for screen readers
    document.querySelectorAll('button[name="previous"]').forEach(function(btn) {
      if (!btn.hasAttribute('aria-label')) {
        btn.setAttribute('aria-label', 'View previous image');
      }
    });
    
    document.querySelectorAll('button[name="next"]').forEach(function(btn) {
      if (!btn.hasAttribute('aria-label')) {
        btn.setAttribute('aria-label', 'View next image');
      }
    });
  }
  
  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      addCarouselInstructions();
      labelCarouselButtons();
    });
  } else {
    addCarouselInstructions();
    labelCarouselButtons();
  }
  
  // Run again after a delay for dynamically loaded content
  window.addEventListener('load', function() {
    setTimeout(function() {
      addCarouselInstructions();
      labelCarouselButtons();
    }, 500);
  });
  
  // Watch for new carousels being added (e.g., quick view modals, AJAX-loaded content)
  if (typeof MutationObserver !== 'undefined') {
    const bodyObserver = new MutationObserver(function(mutations) {
      let shouldRun = false;
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            // Check if the added node is a carousel or contains carousels
            if (node.matches('media-gallery, slider-component') || 
                node.querySelector('media-gallery, slider-component')) {
              shouldRun = true;
            }
          }
        });
      });
      if (shouldRun) {
        setTimeout(function() {
          addCarouselInstructions();
          labelCarouselButtons();
        }, 100);
      }
    });
    
    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();

// =============================================================================
// End of Carousel Instructions Fix
// =============================================================================




// =============================================================================
// PROVENANCE: Auto-open Sustainability accordion when Trust Badge clicked
// Header Click Method
// =============================================================================
(function() {
  'use strict';
  
  let checkAttempts = 0;
  const maxAttempts = 20;
  
  function setupProvenanceBadgeClick() {
    // Find all Provenance Trust Badges
    const trustBadges = document.querySelectorAll('provenance-trust-badge');
    
    // If badges not found yet, try again
    if (trustBadges.length === 0 && checkAttempts < maxAttempts) {
      checkAttempts++;
      setTimeout(setupProvenanceBadgeClick, 500);
      return;
    }
    
    if (trustBadges.length === 0) {
      console.log('Provenance: No trust badges found');
      return;
    }
    
    console.log('Provenance: Found ' + trustBadges.length + ' trust badge(s)');
    
    // Find the Sustainability accordion in Gem Pages
    const sustainabilityAccordion = Array.from(document.querySelectorAll('.gp-accordion-item')).find(function(item) {
      const text = item.textContent || '';
      return text.toUpperCase().includes('SUSTAINABILITY');
    });
    
    if (!sustainabilityAccordion) {
      console.log('Provenance: Sustainability accordion not found');
      return;
    }
    
    console.log('Provenance: Found Sustainability accordion');
    
    // Add click handler to each trust badge
    trustBadges.forEach(function(badge) {
      if (badge.hasAttribute('data-provenance-setup')) {
        return;
      }
      
      badge.setAttribute('data-provenance-setup', 'true');
      badge.setAttribute('onclick', 'openGemPagesSustainability()');
      console.log('Provenance: Added click handler to badge');
    });
  }
  
  // Global function to open the Gem Pages accordion and scroll
  window.openGemPagesSustainability = function() {
    console.log('Provenance: Trust badge clicked, opening accordion...');
    
    // Find the Sustainability accordion
    const sustainabilityAccordion = Array.from(document.querySelectorAll('.gp-accordion-item')).find(function(item) {
      const text = item.textContent || '';
      return text.toUpperCase().includes('SUSTAINABILITY');
    });
    
    if (!sustainabilityAccordion) {
      console.log('Provenance: Could not find Sustainability accordion');
      return;
    }
    
    console.log('Provenance: Found Sustainability accordion');
    
    // Find the accordion body to check if it's closed
    const accordionBody = sustainabilityAccordion.querySelector('.gp-accordion-item_body');
    
    if (!accordionBody) {
      console.log('Provenance: Could not find accordion body');
      return;
    }
    
    const isClosed = accordionBody.classList.contains('gp-overflow-hidden');
    console.log('Provenance: Accordion is ' + (isClosed ? 'closed' : 'open'));
    
    if (isClosed) {
      // Find the clickable header element
      const clickableHeader = sustainabilityAccordion.querySelector('.gp-accordion-item_header.gp-cursor-pointer');
      
      if (clickableHeader) {
        console.log('Provenance: Found clickable header, attempting click');
        
        // Create a real mouse click event
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        
        clickableHeader.dispatchEvent(clickEvent);
        console.log('Provenance: Click event dispatched');
      } else {
        console.log('Provenance: Could not find .gp-accordion-item_header element');
        
        // Fallback: try any element with gp-cursor-pointer in the accordion
        const fallbackClickable = sustainabilityAccordion.querySelector('.gp-cursor-pointer');
        if (fallbackClickable) {
          console.log('Provenance: Trying fallback clickable element');
          fallbackClickable.click();
        }
      }
    } else {
      console.log('Provenance: Accordion already open');
    }
    
    // Scroll to accordion after opening
    setTimeout(function() {
      const offset = 120; // Adjust for sticky header
      const elementPosition = sustainabilityAccordion.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      console.log('Provenance: Scrolled to accordion');
    }, 400);
  };
  
  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupProvenanceBadgeClick);
  } else {
    setupProvenanceBadgeClick();
  }
  
  // Watch for dynamically added badges
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            if (node.matches && node.matches('provenance-trust-badge')) {
              console.log('Provenance: New badge detected');
              setupProvenanceBadgeClick();
            } else if (node.querySelector && node.querySelector('provenance-trust-badge')) {
              console.log('Provenance: New badge detected in content');
              setupProvenanceBadgeClick();
            }
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();
// =============================================================================
// End of Provenance Integration
// =============================================================================



// ADA #150: Make hero banner keyboard accessible
(function() {
  'use strict';
  
  var heroBannerLinks = document.querySelectorAll('[data-hero-banner-link="true"][role="link"]');
  
  heroBannerLinks.forEach(function(banner) {
    // Add keyboard support (Enter key)
    banner.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        banner.click();
      }
    });
  });
})();




// ADA #158: Gem Pages Carousel Accessibility Fix
document.addEventListener('DOMContentLoaded', function() {
  console.log('ADA #158: Starting carousel accessibility fix');
  
  function fixCarousel(carouselId) {
    const carousel = document.querySelector(`#${carouselId}`);
    if (!carousel) {
      console.log(`Carousel ${carouselId} not found`);
      return;
    }
    
    console.log(`Found carousel: ${carouselId}`);
    
    function updateSlides() {
      const slides = carousel.querySelectorAll('.gem-slider-item');
      console.log(`Updating ${slides.length} slides`);
      
      slides.forEach((slide, index) => {
        const slideRect = slide.getBoundingClientRect();
        const carouselRect = carousel.getBoundingClientRect();
        
        const isVisible = (
          slideRect.left >= carouselRect.left - slideRect.width / 2 &&
          slideRect.right <= carouselRect.right + slideRect.width / 2
        );
        
        const links = slide.querySelectorAll('a, button');
        
        if (isVisible) {
          console.log(`Slide ${index} is VISIBLE - making links focusable`);
          slide.setAttribute('aria-hidden', 'false');
          links.forEach(link => {
            link.removeAttribute('tabindex');
            link.removeAttribute('aria-hidden');
          });
        } else {
          console.log(`Slide ${index} is HIDDEN - removing from tab order`);
          slide.setAttribute('aria-hidden', 'true');
          links.forEach(link => {
            link.setAttribute('tabindex', '-1');
            link.setAttribute('aria-hidden', 'true');
          });
        }
      });
    }
    
    // Run immediately
    setTimeout(updateSlides, 500);
    
    // Listen for navigation clicks
    const prevBtn = carousel.closest('.gp-carousel-wrapper')?.querySelector('.gp-carousel-action-back');
    const nextBtn = carousel.closest('.gp-carousel-wrapper')?.querySelector('.gp-carousel-action-next');
    
    if (prevBtn) {
      prevBtn.addEventListener('click', () => setTimeout(updateSlides, 600));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => setTimeout(updateSlides, 600));
    }
    
    // Listen for scroll
    let scrollTimeout;
    carousel.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateSlides, 300);
    });
  }
  
  // Fix both carousels
  fixCarousel('gp-carousel-gDLE4bNdOP'); // Desktop/Tablet
  fixCarousel('gp-carousel-gb9J3T0KyL'); // Mobile
});


// =============================================================================
// PROVENANCE: Auto-open Sustainability tab when Trust Badge clicked
// =============================================================================
(function() {
  'use strict';
  
  function setupProvenanceBadgeClick() {
    // Find all Provenance Trust Badges
    const trustBadges = document.querySelectorAll('provenance-trust-badge');
    
    if (trustBadges.length === 0) return;
    
    // Find the Sustainability collapsible tab
    const sustainabilityTab = Array.from(document.querySelectorAll('.product__accordion details')).find(function(details) {
      const heading = details.querySelector('.accordion__title');
      return heading && heading.textContent.trim() === 'Sustainability';
    });
    
    if (!sustainabilityTab) return;
    
    // Get the accordion content ID
    const accordionContent = sustainabilityTab.querySelector('.accordion__content');
    const contentId = accordionContent ? accordionContent.id : null;
    
    if (!contentId) return;
    
    // Add click handler to each trust badge
    trustBadges.forEach(function(badge) {
      badge.setAttribute('onclick', "openSustainabilityTab('" + contentId + "')");
    });
  }
  
  // Global function to open the tab and scroll
  window.openSustainabilityTab = function(contentId) {
    // Find the Sustainability tab
    const sustainabilityTab = Array.from(document.querySelectorAll('.product__accordion details')).find(function(details) {
      const heading = details.querySelector('.accordion__title');
      return heading && heading.textContent.trim() === 'Sustainability';
    });
    
    if (!sustainabilityTab) return;
    
    // Open the accordion
    sustainabilityTab.setAttribute('open', '');
    
    // Scroll to the content after short delay
    setTimeout(function() {
      const content = document.getElementById(contentId);
      if (content) {
        const offset = 100; // Adjust if you have a sticky header
        const elementPosition = content.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  
  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupProvenanceBadgeClick);
  } else {
    setupProvenanceBadgeClick();
  }
  
  // Run after delay for dynamic content
  setTimeout(setupProvenanceBadgeClick, 1000);
  
  // Watch for dynamically added badges
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function() {
      setupProvenanceBadgeClick();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();
// =============================================================================
